import { useState } from 'react';
import { Loader2, Search, ChevronDown, ChevronRight, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { resolveDepositDisplayStatus } from '../../lib/depositStatus';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function Kv({ label, value, mono = false }) {
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-muted-foreground min-w-[180px] shrink-0">{label}:</span>
      <span className={mono ? 'font-mono' : ''}>{value ?? 'n/a'}</span>
    </div>
  );
}

function fmt(v) {
  if (v == null) return 'n/a';
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (isNaN(n)) return String(v);
  return n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function RawInvoiceDoc({ doc, index }) {
  const [open, setOpen] = useState(false);
  const label = doc.bros_id ?? `Invoice ${index + 1}`;
  return (
    <div className="border rounded p-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-xs font-medium w-full text-left"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {label}
      </button>
      {open && (
        <pre className="mt-1 p-2 bg-muted rounded text-[10px] overflow-x-auto whitespace-pre-wrap">
          {JSON.stringify(doc, null, 2)}
        </pre>
      )}
    </div>
  );
}

export function ReconTracePanel() {
  const [enq, setEnq] = useState('ENQ-00148');
  const [displayCur, setDisplayCur] = useState('EUR');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.reconTrace({
        enq,
        display: displayCur,
      });
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const r = result;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Search className="h-5 w-5 text-purple-600" />
          Recon Trace (Per-Booking)
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Traces a single booking's closed value through every builder that could
          produce it, and names which one matches the displayed number.
          Read-only, no writes.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            <label className="text-sm text-muted-foreground">ENQ:</label>
            <input
              type="text"
              value={enq}
              onChange={(e) => setEnq(e.target.value)}
              className="border rounded px-2 py-1 text-sm font-mono w-36"
              placeholder="ENQ-00148"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-sm text-muted-foreground">Display:</label>
            <select
              value={displayCur}
              onChange={(e) => setDisplayCur(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            >
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
            </select>
          </div>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Trace
          </Button>
        </div>

        {r && (
          <div className="space-y-4">
            <div className="text-xs text-muted-foreground">
              Generated {r.generated_at} | {r.enq} | {r.display_currency}
            </div>

            {/* Deploy */}
            <div className="border rounded-lg p-3 space-y-1">
              <p className="text-sm font-medium">Deploy</p>
              <Kv label="BUILD_COMMIT" value={r.deploy?.build_commit} mono />
              <Kv label="Source" value={r.deploy?.build_commit_source} />
              <Kv label="Resolved at" value={r.deploy?.resolved_at} mono />
              <p className="text-xs text-muted-foreground mt-1">{r.deploy?.note}</p>
            </div>

            {/* Stored */}
            <div className="border rounded-lg p-3 space-y-2">
              <p className="text-sm font-medium">Stored Fields</p>
              {r.stored?.booking ? (
                <div className="space-y-1">
                  <Kv label="bros_id" value={r.stored.booking.bros_id} mono />
                  <Kv label="client_total" value={r.stored.booking.client_total} mono />
                  <Kv label="vat" value={r.stored.booking.vat} mono />
                  <Kv label="vat_rate" value={r.stored.booking.vat_rate} mono />
                  <Kv label="supplier_total" value={r.stored.booking.supplier_total} mono />
                  <Kv label="margin" value={r.stored.booking.margin} mono />
                  <Kv label="currency" value={r.stored.booking.currency} />
                  <Kv label="status" value={r.stored.booking.status} />
                  <Kv label="created_at" value={r.stored.booking.created_at} mono />
                </div>
              ) : (
                <p className="text-sm text-red-600">Booking not found</p>
              )}
              {r.stored?.deposit && (
                <div className="mt-3 border rounded p-2 bg-blue-50/50 dark:bg-blue-950/20 space-y-1">
                  <p className="text-xs font-medium text-blue-800 dark:text-blue-300">Deposit</p>
                  <Kv label="client_deposit_status" value={r.stored.deposit.client_deposit_status} />
                  <Kv label="client_cost (ex-VAT)" value={r.stored.deposit.client_cost} mono />
                  <Kv label="client_deposit_collected_at" value={r.stored.deposit.client_deposit_collected_at} mono />
                  <Kv label="client_deposit_returned_at" value={r.stored.deposit.client_deposit_returned_at} mono />
                  <Kv label="security_deposit_status" value={r.stored.deposit.security_deposit_status} />
                  <Kv label="supplier_cost" value={r.stored.deposit.supplier_cost} mono />
                  <Kv label="security_deposit_paid_at" value={r.stored.deposit.security_deposit_paid_at} mono />
                </div>
              )}
              {r.stored?.invoices && r.stored.invoices.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-muted-foreground mb-1">
                    Matched Invoices ({r.stored.invoices.length})
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-1.5">Invoice ID</th>
                          <th className="text-left p-1.5">Booking ID</th>
                          <th className="text-right p-1.5">Total Amount</th>
                          <th className="text-left p-1.5">Currency</th>
                          <th className="text-left p-1.5">Status</th>
                          <th className="text-left p-1.5">Issue Date</th>
                          <th className="text-left p-1.5">Type</th>
                          <th className="text-center p-1.5">PDF</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.stored.invoices.map((inv, i) => (
                          <tr key={i} className="border-t">
                            <td className="p-1.5 font-mono">{inv.bros_id}</td>
                            <td className="p-1.5 font-mono">{inv.booking_id}</td>
                            <td className="p-1.5 text-right font-mono">{inv.total_amount}</td>
                            <td className="p-1.5">{inv.currency}</td>
                            <td className="p-1.5">{inv.status}</td>
                            <td className="p-1.5">{inv.issue_date}</td>
                            <td className="p-1.5">{inv.type}</td>
                            <td className="p-1.5 text-center">
                              {inv.has_xero_pdf ? (
                                <button
                                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                  onClick={async () => {
                                    try {
                                      const res = await diagnosticsAPI.fetchBrosInvoiceXeroPdf(inv.bros_id);
                                      const blob = new Blob([res.data], { type: 'application/pdf' });
                                      const url = URL.createObjectURL(blob);
                                      window.open(url, '_blank');
                                    } catch (err) {
                                      toast.error(getErrorMessage(err) || 'Failed to fetch PDF');
                                    }
                                  }}
                                >
                                  <FileText className="h-3 w-3" />
                                  View PDF
                                </button>
                              ) : (
                                <span className="text-xs text-muted-foreground cursor-default" title="No Xero PDF on this invoice">
                                  <FileText className="h-3 w-3 inline opacity-30" />
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {r.stored?.invoices && r.stored.invoices.length === 0 && (
                <p className="text-xs text-muted-foreground">No matched invoices</p>
              )}
              {r.stored?.matched_invoices_raw && r.stored.matched_invoices_raw.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-muted-foreground mb-1">
                    Full Invoice Documents ({r.stored.matched_invoices_raw.length})
                  </p>
                  <div className="space-y-1">
                    {r.stored.matched_invoices_raw.map((doc, i) => (
                      <RawInvoiceDoc key={i} doc={doc} index={i} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Deposit Billed Decision */}
            {r.deposit_billed_decision && (
              <div className="border rounded-lg p-3 space-y-3">
                <p className="text-sm font-medium">Deposit Billed Decision</p>
                <div className="flex items-center gap-3 flex-wrap">
                  <Badge
                    variant={r.deposit_billed_decision.billed ? 'default' : 'destructive'}
                    className="text-sm px-3 py-1"
                  >
                    {r.deposit_billed_decision.billed ? 'BILLED' : 'NOT BILLED'}
                  </Badge>
                  <span className="text-xs text-muted-foreground font-mono">
                    build: {r.deploy?.build_commit ?? 'n/a'}
                  </span>
                </div>
                <div className={`border rounded p-2 ${
                  r.deposit_billed_decision.billed
                    ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800'
                    : 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800'
                }`}>
                  <p className="text-xs font-medium mb-1">Reason</p>
                  <p className="text-sm font-mono">{r.deposit_billed_decision.reason}</p>
                </div>
                <Kv label="amount_from_line_items" value={r.deposit_billed_decision.amount_from_line_items} mono />
                {r.deposit_billed_decision.matched_invoice_id && (
                  <Kv label="matched_invoice_id" value={r.deposit_billed_decision.matched_invoice_id} mono />
                )}
                {r.deposit_billed_decision.deposit_line_item_raw && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Raw security_deposit line item</p>
                    <pre className="p-2 bg-muted rounded text-xs overflow-x-auto whitespace-pre-wrap">
                      {JSON.stringify(r.deposit_billed_decision.deposit_line_item_raw, null, 2)}
                    </pre>
                  </div>
                )}
                {r.deposit_billed_decision.candidates?.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">
                      Candidates ({r.deposit_billed_decision.candidates.length})
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left p-1.5">Invoice ID</th>
                            <th className="text-right p-1.5">Subtotal (native)</th>
                            <th className="text-right p-1.5">Subtotal (converted)</th>
                            <th className="text-right p-1.5">Tax Amount</th>
                            <th className="text-left p-1.5">Zero-VAT?</th>
                            <th className="text-left p-1.5">Matches Amount?</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.deposit_billed_decision.candidates.map((c, i) => (
                            <tr key={i} className="border-t">
                              <td className="p-1.5 font-mono">{c.invoice_id}</td>
                              <td className="p-1.5 text-right font-mono">{c.subtotal_native}</td>
                              <td className="p-1.5 text-right font-mono">{c.subtotal_converted}</td>
                              <td className="p-1.5 text-right font-mono">{c.tax_amount}</td>
                              <td className="p-1.5">
                                {c.is_zero_vat ? (
                                  <Badge variant="outline" className="text-xs">Yes</Badge>
                                ) : (
                                  <Badge variant="secondary" className="text-xs">No</Badge>
                                )}
                              </td>
                              <td className="p-1.5">
                                {c.matches_amount ? (
                                  <Badge variant="default" className="text-xs">Yes</Badge>
                                ) : (
                                  <Badge variant="destructive" className="text-xs">No</Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Sibling Bookings */}
            {r.stored?.sibling_bookings && r.stored.sibling_bookings.length > 0 && (
              <div className="border rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium">Sibling Bookings ({r.stored.sibling_bookings.length})</p>
                <p className="text-xs text-muted-foreground">
                  All bookings sharing the same root ENQ ID.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-1.5">Booking ID</th>
                        <th className="text-right p-1.5">Client Total</th>
                        <th className="text-right p-1.5">VAT</th>
                        <th className="text-left p-1.5">Status</th>
                        <th className="text-left p-1.5">Check-in</th>
                        <th className="text-left p-1.5">Check-out</th>
                        <th className="text-right p-1.5">Deposit</th>
                        <th className="text-left p-1.5">Deposit Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.stored.sibling_bookings.map((sb, i) => (
                        <tr key={i} className="border-t">
                          <td className="p-1.5 font-mono">{sb.bros_id}</td>
                          <td className="p-1.5 text-right font-mono">{sb.client_total}</td>
                          <td className="p-1.5 text-right font-mono">{sb.vat}</td>
                          <td className="p-1.5">{sb.status}</td>
                          <td className="p-1.5">{sb.check_in}</td>
                          <td className="p-1.5">{sb.check_out}</td>
                          <td className="p-1.5 text-right font-mono">{sb.deposit?.client_cost ?? 'n/a'}</td>
                          <td className="p-1.5">{sb.deposit ? resolveDepositDisplayStatus(sb.deposit) : 'n/a'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Builders */}
            <div className="border rounded-lg p-3 space-y-2">
              <p className="text-sm font-medium">Builders</p>
              {r.builders && r.builders.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-1.5">Builder</th>
                        <th className="text-left p-1.5">File:Line</th>
                        <th className="text-right p-1.5">Closed</th>
                        <th className="text-right p-1.5">Invoiced</th>
                        <th className="text-right p-1.5">Delta</th>
                        <th className="text-left p-1.5">FX Round-trip?</th>
                        <th className="text-left p-1.5">Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.builders.map((b, i) => (
                        <tr key={i} className="border-t">
                          <td className="p-1.5 font-mono font-medium">{b.name}</td>
                          <td className="p-1.5 font-mono text-muted-foreground">{b.file_line}</td>
                          <td className="p-1.5 text-right font-mono">{fmt(b.closed_value)}</td>
                          <td className="p-1.5 text-right font-mono">{fmt(b.invoiced_value)}</td>
                          <td className={`p-1.5 text-right font-mono ${b.delta != null && b.delta !== 0 ? 'text-red-600' : ''}`}>
                            {fmt(b.delta)}
                          </td>
                          <td className="p-1.5">
                            {b.fx?.round_tripped_through_gbp ? (
                              <Badge variant="destructive" className="text-xs">Yes</Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs">No</Badge>
                            )}
                          </td>
                          <td className="p-1.5 font-mono text-xs">{b.fx?.rate_str}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No builders produced output</p>
              )}
            </div>

            {/* Verdict */}
            <div className={`border rounded-lg p-3 ${
              r.verdict?.includes('DISAGREE') ? 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800' :
              r.verdict?.includes('agree') ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' :
              'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800'
            }`}>
              <p className="text-sm font-medium">Verdict</p>
              <p className="text-sm mt-1">{r.verdict}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
